import throttle from "lodash/throttle";
import { Store } from "redux";
import { trackEvent } from "../api/tracker";
import { getMeristemContext } from "../meristemContext";
import { ReducerStore } from "./store";

export type Serializer<
  K extends keyof ReducerStore = keyof ReducerStore,
  T = ReducerStore[K]
> = {
  save: (state: T) => T;
  load: (storedState: T) => T;
};

const persistSerializers: Partial<
  Record<keyof ReducerStore, boolean | Serializer>
> = {
  surveyAnswers: true,
  routeId: true,
  linearBuyflow: true,
  activeSubscriptionData: true,
  upid: true,
};

// To maintain backwards-compatibility with redux-persist, use the same key.
const PERSIST_KEY = "persist:root";

export function rehydrateStore() {
  const state = {};
  try {
    const storedValue = localStorage.getItem(PERSIST_KEY);
    if (storedValue) {
      const parsed = JSON.parse(storedValue);
      // Again, maintaining backward-compat with redux-persist, which stringified the individual
      // keys as well
      Object.keys(persistSerializers).forEach((key) => {
        const serializer = persistSerializers[key];

        if (parsed[key]) {
          state[key] = JSON.parse(parsed[key]);
        }
        if (serializer !== true) {
          state[key] = serializer.load(state[key]);
        }
      });
    }
    // Special case: routeId
    // Meristem context is the source of truth for this, but sometimes unavailable and thus
    // persisted. FIXME: It would probably make sense to use a different mechanism for this, and
    // perhaps not include it in Redux, since it's injected directly into the page.
    const meristemContext = getMeristemContext();
    // Always honor meristem context, we only serialize for cases where meristem can not
    // infer directly.
    if (meristemContext?.route_id) {
      (state as any).routeId = meristemContext.route_id;
    }
  } catch (e) {
    trackEvent("onPersistReadError", e);
  }
  return state;
}

export function subscribePersistence(store: Store, throttleTime = 0) {
  store.subscribe(
    throttle(() => {
      const toBeStored = {};
      try {
        Object.keys(persistSerializers).forEach((key) => {
          const serializer = persistSerializers[key];

          let valueToSerialized = store.getState()[key];
          if (typeof serializer === "object") {
            valueToSerialized = serializer.save(valueToSerialized);
          }
          toBeStored[key] = JSON.stringify(valueToSerialized);
        });
        localStorage.setItem(PERSIST_KEY, JSON.stringify(toBeStored));
      } catch (e) {
        // This can happen on in-app webviews that don't have access to localStorage.
        trackEvent("onPersistWriteError", e);
        // If this happens while developing, more likely to be an unserializable value in the store;
        // explode.
        /* istanbul ignore next sanity */
        if (process.env.NODE_ENV !== "production") {
          throw new Error("Unserializable value in store");
        }
      }
    }, throttleTime)
  );
}
